import { useAutoOpenChat } from 'modules/ai/hooks/useAutoOpenChat'
import { getRenderedDefFromTableId } from 'modules/ai_reports/components/DatasourceTypeRenderer/tableRendererRegistry'
import { DateNavigationFilter } from 'modules/ai_reports/components/DateNavigationFilter'
import { Layout } from 'modules/ai_reports/components/Layout'
import { ReportWidget } from 'modules/ai_reports/components/ReportWidget'
import { SaveModal } from 'modules/ai_reports/components/SaveModal'
import { ReportType } from 'modules/ai_reports/hooks/useReportCoreState'
import {
  getDateNavigatorConfigArg,
  getInitialDateFilter,
  useReportState,
} from 'modules/ai_reports/hooks/useReportState'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type ReportDetailProps = {
  report: ReportType
}

export const ReportDetail = ({ report }: ReportDetailProps) => {
  const { state, dispatch, agentState, NavConfirmModal, saveReport, handleExport } = useReportState(
    {
      datasourceId: report.datasource?.id || '',
      initialReport: report,
      initialDateFilter: getInitialDateFilter(report.filters),
      initialFilters: report.filters,
      initialColumnFormatHints: report.formatHints.map((hint) => ({
        column_alias: hint.columnDisplayName,
        column_format: hint.formatHint,
      })),
      reportName: report.name,
    }
  )

  useAutoOpenChat()

  const rendererDef = getRenderedDefFromTableId(report.datasource?.id || '')
  const dateNavigatorConfig = rendererDef?.dateNavigatorConfig?.(
    getDateNavigatorConfigArg(report.filters)
  )

  return (
    <>
      <ResponsivePage.Body
        variant="fullscreen"
        onActionClick={(key) => {
          switch (key) {
            case 'export':
              handleExport()
              break
            case 'save':
              dispatch({ type: 'UNSAVED_CHANGES_CONFIRM_REQUESTED' })
              break
          }
        }}
      >
        <Layout
          header={
            dateNavigatorConfig && (
              <DateNavigationFilter
                config={dateNavigatorConfig}
                onSelect={(value) => {
                  dispatch({ type: 'DATE_FILTER_UPDATED', payload: value?.value })
                }}
                value={
                  state.dateFilter
                    ? {
                        value: {
                          from: state.dateFilter.from,
                          to: state.dateFilter.to ?? state.dateFilter.from,
                        },
                        granularity: dateNavigatorConfig.granularity,
                      }
                    : undefined
                }
              />
            )
          }
          paddingX="s16"
        >
          {state.report &&
            state.report.widgets.map((widget, index) => {
              const currentReport = state.report
              if (!currentReport) return null

              return (
                <ReportWidget
                  id={currentReport.id}
                  key={index}
                  sqlQuery={widget.query}
                  description={widget.description}
                  dateFilter={state.dateFilter}
                  tableId={currentReport.datasource?.id || ''}
                  columnFormatHints={agentState?.sqlAnswer?.column_format_hints ?? []}
                  reportName={currentReport.name}
                  currentFilters={state.datatableFilters ?? undefined}
                  onStateChange={(s) => {
                    if (s.filters) {
                      dispatch({ type: 'DATACOLLECTION_FILTERS_UPDATED', payload: s.filters })
                    }
                  }}
                />
              )
            })}
          <NavConfirmModal />
          <SaveModal
            showSaveAsNewReport
            open={state.saveModalOpen}
            name={state.report?.name}
            description={state.report?.description}
            onClose={() => dispatch({ type: 'UNSAVED_CHANGES_DISMISSED' })}
            onClickSave={({ name, description, saveAsNew }) =>
              saveReport({ name, description, saveAsNew })
            }
          />
        </Layout>
      </ResponsivePage.Body>
    </>
  )
}
